import { ENVIRONMENT_URL, DATAVERSE_TABLES } from '../constants/dataverse';

const environmentUrl = ENVIRONMENT_URL;
/**
 * Service for creating or fetching Department Plant Tour ID
 * This service handles department tours for non-Quality departments
 */

// Environment URL
// const environmentUrl = "https://org487f0635.crm8.dynamics.com";dev
export interface DepartmentTourParams {
  accessToken: string;
  departmentId: string;
  employeeName: string;
  roleName: string;
  plantId: string;
  userRoleID: string;
}

export interface DepartmentTourResponse {
  cr3ea_prod_departmenttourid?: string;
  cr3ea_departmenttourid?: string;
  cr3ea_departmenttour_id?: string;
  id?: string;
  cr3ea_title: string;
  cr3ea_status: string;
  cr3ea_departmentid: string;
  cr3ea_plantid: string;
  cr3ea_tourstartdate: string;
  cr3ea_tourby: string;
  cr3ea_observedby: string;
  cr3ea_roleid: string;
}

/**
 * Creates or fetches a Department Plant Tour ID
 * @param params - Department tour parameters
 * @returns Promise<string | null> - Department tour ID or null if failed
 */
export async function createOrFetchDepartmentTour({
  accessToken,
  departmentId,
  employeeName,
  roleName,
  plantId,
  userRoleID,
}: DepartmentTourParams): Promise<string | null> {
  if (!accessToken) {
    console.error('Access token is required for department tour creation');
    return null;
  }

  if (!departmentId || !employeeName || !roleName || !plantId || !userRoleID) {
    console.error('Missing required parameters for department tour creation');
    return null;
  }

  const apiVersion = "9.2";
  const candidateTables = Array.from(new Set([
    DATAVERSE_TABLES.DEPARTMENT_TOUR,
    'cr3ea_prod_departmenttours',
    'cr3ea_departmenttours'
  ]));

  const now = new Date();
  const pad = (n: number) => n.toString().padStart(2, "0");
  const tourStartDate = `${pad(now.getMonth() + 1)}-${pad(now.getDate())}-${now.getFullYear()} ${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(now.getSeconds())}`;
  const title = `${roleName}_${pad(now.getMonth() + 1)}-${pad(now.getDate())}-${now.getFullYear()}`;

  const headers = {
    Accept: "application/json",
    "Content-Type": "application/json; charset=utf-8",
    "OData-MaxVersion": "4.0",
    "OData-Version": "4.0",
    Prefer: "return=representation",
    Authorization: `Bearer ${accessToken}`,
  };

  const dataToSave = {
    cr3ea_departmentid: departmentId.toString(),
    cr3ea_tourstartdate: tourStartDate,
    cr3ea_tourby: employeeName,
    cr3ea_status: "In Progress",
    cr3ea_plantid: plantId.toString(),
    cr3ea_observedby: employeeName,
    cr3ea_roleid: userRoleID.toString(),
    cr3ea_title: title,
  };

  let lastError: Error | null = null;

  for (const tableName of candidateTables) {
    const apiUrl = `${environmentUrl}/api/data/v${apiVersion}/${tableName}`;
    
    // Check if a department tour is already in progress
    const checkUrl = `${apiUrl}?$filter=cr3ea_status eq 'In Progress' and cr3ea_departmentid eq '${departmentId}'&$top=1`;
    try {
      const existingTourResponse = await fetch(checkUrl, {
        method: "GET",
        headers,
      });

      if (existingTourResponse.status === 404) {
        console.warn(`Department Tour table ${tableName} not found (404), trying next candidate...`);
        continue;
      }

      if (existingTourResponse.ok) {
        const existingTourData = await existingTourResponse.json();
        if (existingTourData.value && existingTourData.value.length > 0) {
          const existingTour = existingTourData.value[0];
          const tourId = existingTour.cr3ea_prod_departmenttourid || existingTour.cr3ea_departmenttourid || existingTour.cr3ea_departmenttour_id || existingTour.id;
          return tourId;
        }

        // Create new department tour
        const createResponse = await fetch(apiUrl, {
          method: "POST",
          headers,
          body: JSON.stringify(dataToSave),
        });

        if (createResponse.ok) {
          const newTourData = await createResponse.json();
          const tourId = newTourData.cr3ea_prod_departmenttourid || newTourData.cr3ea_departmenttourid || newTourData.cr3ea_departmenttour_id || newTourData.id;
          if (tourId) return tourId;
        } else {
          const errorText = await createResponse.text();
          lastError = new Error(`Error creating department tour: ${createResponse.status} ${createResponse.statusText} - ${errorText}`);
        }
      } else {
        const errorText = await existingTourResponse.text();
        lastError = new Error(`Error checking department tour: ${existingTourResponse.status} - ${errorText}`);
      }
    } catch (err: any) {
      console.error(`Error querying candidate table ${tableName}:`, err);
      lastError = err;
    }
  }

  if (lastError) throw lastError;
  return null;
}

/**
 * Updates department tour status
 * @param accessToken - Access token
 * @param departmentTourId - Department tour ID
 * @param status - New status
 * @returns Promise<boolean> - Success status
 */
export async function updateDepartmentTourStatus(
  accessToken: string,
  departmentTourId: string,
  status: string
): Promise<boolean> {
  if (!accessToken || !departmentTourId || !status) {
    console.error('Missing required parameters for department tour status update');
    return false;
  }

  const apiVersion = "9.2";
  const tableName = DATAVERSE_TABLES.DEPARTMENT_TOUR;
  const apiUrl = `${environmentUrl}/api/data/v${apiVersion}/${tableName}(${departmentTourId})`;

  const headers = {
    Accept: "application/json",
    "Content-Type": "application/json; charset=utf-8",
    "OData-MaxVersion": "4.0",
    "OData-Version": "4.0",
    Prefer: "return=representation",
    Authorization: `Bearer ${accessToken}`,
  };

  const updateData = {
    cr3ea_status: status,
    cr3ea_tourcompletiondate: status === 'Completed' ? new Date().toISOString() : null
  };

  try {
    console.log('Updating department tour status:', { departmentTourId, status });

    const response = await fetch(apiUrl, {
      method: "PATCH",
      headers,
      body: JSON.stringify(updateData),
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Error updating department tour status:', errorText);
      throw new Error(`Error updating department tour status: ${response.statusText} - ${errorText}`);
    }

    const result = await response.json();
    console.log('Department tour status updated successfully:', result);
    return true;

  } catch (error) {
    console.error('Error in updateDepartmentTourStatus:', error);
    return false;
  }
}
